import { cn } from '../../lib/utils';

// Modern pulse loader with BrainShift branding
export function BrainShiftLoader({ className, size = 'default', text = 'Loading...' }) {
  const sizeClasses = {
    small: 'w-8 h-8',
    default: 'w-12 h-12',
    large: 'w-16 h-16',
  };

  return (
    <div className={cn('flex flex-col items-center justify-center gap-4', className)}>
      <div className="relative">
        {/* Outer ring */}
        <div className={cn(
          'rounded-full border-4 border-muted animate-pulse',
          sizeClasses[size]
        )} />
        
        {/* Spinning arc */}
        <div className={cn(
          'absolute inset-0 rounded-full border-4 border-transparent border-t-primary animate-spin',
          sizeClasses[size]
        )} />
        
        {/* Inner dot */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
        </div>
      </div>
      
      {text && (
        <p className="text-sm text-muted-foreground animate-pulse">{text}</p>
      )}
    </div>
  );
}

// Modern skeleton loader for content
export function SkeletonLoader({ className, variant = 'default' }) {
  if (variant === 'card') {
    return (
      <div className={cn('space-y-4 p-6 border rounded-lg', className)}>
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-muted animate-pulse" />
          <div className="space-y-2 flex-1">
            <div className="h-4 w-1/3 bg-muted rounded animate-pulse" />
            <div className="h-3 w-1/2 bg-muted rounded animate-pulse" />
          </div>
        </div>
        <div className="space-y-2">
          <div className="h-3 w-full bg-muted rounded animate-pulse" />
          <div className="h-3 w-4/5 bg-muted rounded animate-pulse" />
        </div>
      </div>
    );
  }

  if (variant === 'table-row') {
    return (
      <div className={cn('flex items-center gap-4 p-4 border-b', className)}>
        <div className="w-8 h-8 rounded bg-muted animate-pulse" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-1/4 bg-muted rounded animate-pulse" />
          <div className="h-3 w-1/3 bg-muted rounded animate-pulse" />
        </div>
        <div className="h-6 w-16 bg-muted rounded animate-pulse" />
        <div className="h-6 w-20 bg-muted rounded animate-pulse" />
      </div>
    );
  }

  if (variant === 'stats') {
    return (
      <div className={cn('grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4', className)}>
        {[...Array(4)].map((_, i) => (
          <div key={i} className="p-6 border rounded-lg space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-muted animate-pulse" />
              <div className="space-y-2 flex-1">
                <div className="h-3 w-20 bg-muted rounded animate-pulse" />
                <div className="h-6 w-12 bg-muted rounded animate-pulse" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={cn('space-y-3', className)}>
      <div className="h-4 w-full bg-muted rounded animate-pulse" />
      <div className="h-4 w-4/5 bg-muted rounded animate-pulse" />
      <div className="h-4 w-3/5 bg-muted rounded animate-pulse" />
    </div>
  );
}

// Modern dots loader
export function DotsLoader({ className }) {
  return (
    <div className={cn('flex items-center gap-1', className)}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="w-2 h-2 bg-primary rounded-full animate-bounce"
          style={{ animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </div>
  );
}

// Full page loader
export function PageLoader({ text = 'Loading...' }) {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center">
        {/* Animated logo */}
        <div className="relative w-16 h-16 mx-auto mb-6">
          {/* Background circle */}
          <div className="absolute inset-0 rounded-xl bg-primary/10 animate-pulse" />
          
          {/* Rotating border */}
          <svg className="absolute inset-0 w-16 h-16 animate-spin-slow" viewBox="0 0 64 64">
            <circle
              cx="32"
              cy="32"
              r="28"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeDasharray="80 100"
              className="text-primary"
            />
          </svg>
          
          {/* Inner icon */}
          <div className="absolute inset-0 flex items-center justify-center">
            <svg 
              className="w-8 h-8 text-primary" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" 
              />
            </svg>
          </div>
        </div>
        
        {/* Text with gradient */}
        <h3 className="text-lg font-semibold font-['Manrope'] bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
          {text}
        </h3>
        
        {/* Animated bar */}
        <div className="mt-4 w-48 h-1 mx-auto bg-muted rounded-full overflow-hidden">
          <div className="h-full w-1/3 bg-primary rounded-full animate-loading-bar" />
        </div>
      </div>
    </div>
  );
}

// Inline button loader
export function ButtonLoader({ className }) {
  return (
    <svg 
      className={cn('animate-spin h-4 w-4', className)} 
      xmlns="http://www.w3.org/2000/svg" 
      fill="none" 
      viewBox="0 0 24 24"
    >
      <circle 
        className="opacity-25" 
        cx="12" 
        cy="12" 
        r="10" 
        stroke="currentColor" 
        strokeWidth="4"
      />
      <path 
        className="opacity-75" 
        fill="currentColor" 
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}
